import { Target, Tv, BarChart3, Share2, MapPin, Handshake, Zap, Home, Smartphone, Film, Clock, RefreshCw, Shield, DollarSign, FileText, Users, Palette, Video, TrendingUp, Map, Car, Building, Award, Briefcase, Phone, CheckCircle } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export interface ServiceFeature {
  icon: LucideIcon;
  title: string;
  description: string;
}

export interface ProcessStep {
  title: string;
  description: string;
}

export interface ServiceData {
  id: string;
  icon: LucideIcon;
  title: string;
  tagline: string;
  shortDescription: string;
  overview: string;
  processSteps: ProcessStep[];
  features: ServiceFeature[];
  governmentUseCases: string;
  businessUseCases: string;
  channels?: string[];
  useCaseExamples: string[];
}

export const servicesData: ServiceData[] = [
  {
    id: 'paid-media',
    icon: Target,
    title: 'Paid Media & Media Buying',
    tagline: 'Strategic placement across broadcast and digital channels',
    shortDescription: 'Strategic placement across broadcast, streaming, and digital channels to maximize reach.',
    overview: 'We negotiate and place media buys across premium broadcast networks, streaming platforms, and digital channels. Our buying power, combined with data-driven strategy, ensures your message reaches the right audience at the right time without wasted impressions.',
    processSteps: [
      { title: 'Audience Analysis', description: 'Define who you want to reach' },
      { title: 'Channel Strategy', description: 'Select best channels for your goals' },
      { title: 'Media Buy', description: 'Negotiate rates and placements' },
      { title: 'Optimize & Report', description: 'Monitor KPIs and adjust in real-time' },
    ],
    features: [
      { icon: BarChart3, title: 'Data-Driven Targeting', description: 'Audience segmentation by demographics, interests, and behavior' },
      { icon: Tv, title: 'Premium Inventory', description: 'Access to FOX, ABC, NBC, CBS, and 500+ streaming apps' },
      { icon: DollarSign, title: 'Negotiated Rates', description: 'Leverage our buying relationships for better CPMs' },
      { icon: MapPin, title: 'Geo-Precision', description: 'Target by DMA, city, ZIP code, or custom polygons' },
      { icon: TrendingUp, title: 'Real-Time Optimization', description: 'Continuous monitoring with mid-flight adjustments' },
      { icon: FileText, title: 'Transparent Reporting', description: 'See exactly where your ads ran and how they performed' },
    ],
    governmentUseCases: 'Public awareness campaigns, recruitment, program promotion',
    businessUseCases: 'Brand launches, seasonal pushes, market expansion',
    channels: ['FOX', 'ABC', 'NBC', 'CBS', 'ESPN', 'CNN', 'Hulu', 'Roku', 'Peacock', 'Paramount+'],
    useCaseExamples: [
      'State health department promoting vaccination awareness across 12 counties',
      'Regional car dealership targeting in-market auto shoppers within 30-mile radius',
      'City tourism board driving visitor interest during peak travel planning season',
    ],
  },
  {
    id: 'ott-ctv',
    icon: Tv,
    title: 'OTT/CTV Advertising',
    tagline: 'Reach cord-cutters where they stream',
    shortDescription: 'Reach cord-cutters on Roku, Fire TV, Apple TV, and 500+ streaming apps.',
    overview: 'Over-the-Top (OTT) and Connected TV (CTV) advertising puts your brand on the biggest screen in the house. Target viewers watching their favorite content on Roku, Fire TV, Apple TV, smart TVs, and streaming apps with the same precision as digital advertising.',
    processSteps: [
      { title: 'Audience Definition', description: 'Identify target households' },
      { title: 'Platform Selection', description: 'Choose streaming apps & devices' },
      { title: 'Creative Production', description: 'Develop TV-quality ads' },
      { title: 'Launch & Measure', description: 'Track completion & attribution' },
    ],
    features: [
      { icon: Home, title: 'Household Targeting', description: 'Reach entire households, not just individuals' },
      { icon: Smartphone, title: 'Cross-Device Reach', description: 'Extend campaigns to mobile and desktop for retargeting' },
      { icon: Film, title: 'Premium Content Environment', description: 'Ads appear alongside trusted, brand-safe programming' },
      { icon: Clock, title: 'Non-Skippable Formats', description: '15 and 30-second spots viewers watch to completion' },
      { icon: MapPin, title: 'Hyper-Local Targeting', description: 'Geo-fence specific neighborhoods or points of interest' },
      { icon: RefreshCw, title: 'Frequency Management', description: 'Control how often each household sees your ad' },
    ],
    governmentUseCases: 'Civic engagement, emergency preparedness, public health',
    businessUseCases: 'Local retailers, restaurants, service providers, real estate',
    channels: ['Roku', 'Amazon Fire TV', 'Apple TV', 'Samsung TV+', 'LG Channels', 'Hulu', 'Peacock', 'Tubi', 'Pluto TV', 'Paramount+'],
    useCaseExamples: [
      'County emergency services promoting disaster preparedness to 50,000 households',
      'Local HVAC company targeting homeowners in specific ZIP codes before summer',
      'Regional hospital system building awareness for new specialty center',
    ],
  },
  {
    id: 'programmatic',
    icon: BarChart3,
    title: 'Programmatic Advertising',
    tagline: 'AI-powered ad buying that optimizes in real-time',
    shortDescription: 'AI-powered ad buying that optimizes in real-time across multiple platforms.',
    overview: 'Programmatic advertising automates the media buying process using algorithms and real-time bidding. Instead of manual negotiations, our technology evaluates millions of ad opportunities per second, purchasing only the impressions most likely to drive results for your campaign.',
    processSteps: [
      { title: 'User Visits Site', description: 'Ad opportunity detected' },
      { title: 'AI Evaluates', description: 'Match to target criteria' },
      { title: 'Bid Placed', description: 'In milliseconds' },
      { title: 'Ad Served', description: 'To qualified viewer' },
    ],
    features: [
      { icon: Zap, title: 'Real-Time Bidding', description: 'Automated auctions for every ad impression' },
      { icon: Target, title: 'Precision Targeting', description: 'Layer demographics, behavior, context, and location' },
      { icon: RefreshCw, title: 'Dynamic Optimization', description: 'AI shifts budget to best-performing placements' },
      { icon: BarChart3, title: 'Unified Reporting', description: 'Single dashboard across all programmatic channels' },
      { icon: Shield, title: 'Brand Safety Controls', description: 'Blocklists and contextual filters protect your brand' },
      { icon: DollarSign, title: 'Efficient Spending', description: 'Pay only for impressions that meet your criteria' },
    ],
    governmentUseCases: 'Large-scale awareness campaigns, multi-region targeting',
    businessUseCases: 'Performance-focused campaigns, retargeting, lead generation',
    channels: ['Display Ads', 'Video Pre-Roll', 'Native Ads', 'Audio/Podcast', 'Digital Out-of-Home', 'Connected TV', 'Mobile In-App'],
    useCaseExamples: [
      'Federal agency reaching veterans across multiple DMAs with benefit information',
      'E-commerce retailer retargeting cart abandoners across web and mobile',
      'Professional services firm targeting C-suite decision makers by job title',
    ],
  },
  {
    id: 'social-media',
    icon: Share2,
    title: 'Social Media Advertising',
    tagline: 'Targeted campaigns on Meta, LinkedIn, X, and TikTok',
    shortDescription: 'Targeted campaigns on Meta, LinkedIn, X, and TikTok with full creative support.',
    overview: 'Social media advertising puts your message in the feeds of your ideal audience. We handle strategy, creative, targeting, and optimization across all major platforms, ensuring your campaigns drive engagement, leads, and measurable results.',
    processSteps: [
      { title: 'Strategy Development', description: 'Platform & audience selection' },
      { title: 'Creative Production', description: 'Design scroll-stopping ads' },
      { title: 'Campaign Launch', description: 'Targeted deployment' },
      { title: 'Optimize & Scale', description: 'A/B test and expand winners' },
    ],
    features: [
      { icon: Palette, title: 'Full Creative Services', description: 'We design scroll-stopping ads optimized for each platform' },
      { icon: Users, title: 'Advanced Audience Building', description: 'Custom audiences, lookalikes, and interest targeting' },
      { icon: RefreshCw, title: 'Retargeting Campaigns', description: 'Re-engage website visitors and past customers' },
      { icon: Video, title: 'Video & Story Ads', description: 'Vertical video content for Reels, Stories, and TikTok' },
      { icon: BarChart3, title: 'A/B Testing', description: 'Continuous creative and audience testing for optimization' },
      { icon: TrendingUp, title: 'Conversion Tracking', description: 'Pixel setup and attribution across platforms' },
    ],
    governmentUseCases: 'Citizen engagement, job recruitment, event promotion',
    businessUseCases: 'Lead generation, e-commerce, brand awareness, hiring',
    channels: ['Meta (Facebook & Instagram)', 'LinkedIn', 'X (Twitter)', 'TikTok', 'YouTube', 'Pinterest', 'Snapchat'],
    useCaseExamples: [
      'City government recruiting police officers through targeted LinkedIn and Meta campaigns',
      'Local restaurant chain promoting new location opening to nearby residents',
      'B2B software company generating demo requests from IT decision makers',
    ],
  },
  {
    id: 'geo-targeting',
    icon: MapPin,
    title: 'Geo-Targeted Campaigns',
    tagline: 'Precision targeting by ZIP code, city, or custom boundaries',
    shortDescription: 'Precision targeting by ZIP code, city, DMA, or custom polygons.',
    overview: 'Geo-targeting ensures your advertising budget reaches only the audiences that matter: constituents in specific districts, customers within your service area, or attendees at a particular venue. Eliminate wasted impressions and focus on the locations that drive results.',
    processSteps: [
      { title: 'Define Geography', description: 'Set target boundaries' },
      { title: 'Audience Overlay', description: 'Layer demographic targeting' },
      { title: 'Campaign Execution', description: 'Precise delivery' },
      { title: 'Location Analytics', description: 'Measure foot traffic & visits' },
    ],
    features: [
      { icon: Map, title: 'Multi-Level Geography', description: 'From nationwide to single ZIP code, any scale' },
      { icon: MapPin, title: 'Custom Polygons', description: 'Draw exact boundaries for districts, campuses, or zones' },
      { icon: Target, title: 'Point-of-Interest Targeting', description: 'Reach people near competitors, venues, or landmarks' },
      { icon: Car, title: 'Commuter Targeting', description: 'Target people who travel through specific areas' },
      { icon: Home, title: 'Household-Level Precision', description: 'Addressable advertising to specific homes' },
      { icon: BarChart3, title: 'Location Analytics', description: 'Foot traffic attribution and visit tracking' },
    ],
    governmentUseCases: 'District-specific messaging, voting outreach, zoned services',
    businessUseCases: 'Service area marketing, franchise territories, local competition',
    useCaseExamples: [
      'State representative communicating with constituents in their exact district boundaries',
      'Pizza franchise targeting only households within 5-mile delivery radius',
      'Tourism board geo-fencing airport arrivals to promote local attractions',
    ],
  },
  {
    id: 'subcontracting',
    icon: Handshake,
    title: 'Subcontracting & Partnerships',
    tagline: 'Meet diversity requirements with a certified SDVOSB/MBO partner',
    shortDescription: 'Partner with our SDVOSB/MBO certified agency to meet diversity requirements.',
    overview: 'Yaw CTV is a certified Service-Disabled Veteran-Owned Small Business (SDVOSB) and Minority Business Owner (MBO). Prime contractors and agencies can partner with us to fulfill small business subcontracting requirements while accessing genuine digital advertising expertise, not just a checkbox.',
    processSteps: [
      { title: 'Initial Discussion', description: 'Understand your requirements' },
      { title: 'Partnership Structure', description: 'Define scope & terms' },
      { title: 'Documentation', description: 'Compliance paperwork' },
      { title: 'Execute & Report', description: 'Deliver & document results' },
    ],
    features: [
      { icon: Award, title: 'Verified Certifications', description: 'SAM.gov registered with all documentation current' },
      { icon: FileText, title: 'Transparent Reporting', description: 'Clear deliverables and audit-ready documentation' },
      { icon: Shield, title: 'Compliance Expertise', description: 'Familiar with government reporting requirements' },
      { icon: Briefcase, title: 'Flexible Engagement', description: 'Project-based, retainer, or percentage-of-spend models' },
      { icon: Zap, title: 'Fast Onboarding', description: 'Ready to execute with minimal ramp-up time' },
      { icon: Phone, title: 'Dedicated Support', description: 'Named account manager for every partnership' },
    ],
    governmentUseCases: 'Meeting SDVOSB subcontracting requirements',
    businessUseCases: 'Adding certified partner capabilities',
    channels: ['541810 - Advertising Agencies', '541820 - Public Relations Agencies', '541830 - Media Buying Agencies', '541840 - Media Representatives'],
    useCaseExamples: [
      'Prime contractor fulfilling SDVOSB subcontracting goals on federal IT contract',
      'Marketing agency adding certified small business capabilities for government RFPs',
      'Direct award to qualified small business for agency digital advertising needs',
    ],
  },
];
